<template>
  <div class="channels-content">
    <div class="header-container">
      <span class="title">{{ $t(`${i18nPrefix}.containerTitle.channels`) }}</span>
      <div class="switch-button">
        <SwitchButton
          v-model="channelsType"
          :leftLabel="$t(`${i18nPrefix}.btn.jobs`)"
          leftLabelValue="jobs"
          :rightLabel="$t(`${i18nPrefix}.btn.applications`)"
          rightLabelValue="applications"
          @change="handelChangeTabStatus"
        />
      </div>
    </div>
    <div class="chart-background">
      <div ref="channelChart" class="channels-chart"></div>
    </div>
    <ul class="channels-chart-ul" v-if="!loading && channelsType === 'applications'">
      <li v-for="(item, index) in channelList" v-show="item.total > 0 && item.show" class="channels-chart-li" :key="index">
        <div class="channels-title">
          <span
            class="color"
            :style="'background:' + item.color"
          ></span>
          <span class="name">{{ item.i18nName }}</span>
        </div>
        <el-progress
          class="proportion"
          :text-inside="true"
          :show-text="true"
          :stroke-width="8"
          :percentage="item.percentage"
          :color="item.color"
        ></el-progress>
        <span class="channels-title-number">{{ item.total|formatBigNumber }}</span>
      </li>
    </ul>
    <RectLoading class="loading" v-show="loading"/>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { mapActions, mapGetters } from 'vuex'
import _ from 'lodash'
import moment from 'moment'
import RectLoading from '@/components/loading/rectLoading.vue'
import SwitchButton from './switchButton.vue'
import { addI18nProperty, createI18nProperty } from '@/i18n'
import { formatBigNumber } from '@/components/filters'

const CHART_OPTION = {
  tooltip: {
    trigger: 'axis',
    formatter: function (params) {
      const maxRow = 10
      let contentHtml = ''
      let columnHtml = ''
      let count = 1
      _.forEach(params.reverse(), (item) => {
        if (item.value <= 0) {
          return
        }
        if (count % maxRow === 0) {
          contentHtml += `<div style="display: inline-block;">${columnHtml}</div>`
          columnHtml = ''
        }
        columnHtml += `
          <div style="display: flex; margin: 5px 0; padding: 0 10px; justify-content: space-between; align-items: center;">
            <div style="width: 10px; height: 10px; border-radius: 10px; background-color: ${item.color}; flex-shrink: 0;"></div>
            <div style="margin-left: 10px; flex: 1;">${item.seriesName}</div>
            <div style="margin-left: 5px; flex-shrink: 0;">${item.value}</div>
          </div>
        `
        count += 1
      })
      if (columnHtml) {
        contentHtml += `<div style="display: inline-block;">${columnHtml}</div>`
      }
      return `
      <div class="tooltip-title">${params[0]?.axisValue}</div>
      <div class="tooltip-content">${contentHtml}</div>
      `
    }
  },
  grid: {
    left: '5%',
    right: '15%',
    bottom: '3%',
    containLabel: true
  },
  legend: {
    orient: 'vertical',
    x: 'left',
    top: 60,
    left: '88%',
    data: [],
    formatter: (name) => {
      const maxLength = 10
      if (name.length > maxLength) {
        return name.substring(0, maxLength) + '...'
      }
      return name
    },
    tooltip: {
      show: true,
      formatter: (params) => {
        const html = `
          <div style="font-size: 12px;">${params.name}</div>
        `
        return html
      }
    }
  },
  xAxis: [
    {
      type: 'category',
      boundaryGap: false,
      splitLine: {
        show: true,
        lineStyle: {
          type: 'solid',
          color: '#f9f9f9',
          width: 15
        }
      },
      axisLabel: {
        margin: 15
      },
      data: []
    }
  ],
  yAxis: [
    {
      type: 'value',
      splitLine: {
        show: true,
        lineStyle: {
          cap: 'square',
          type: 'solid',
          color: '#ffffff',
          width: 16
        }
      },
      z: 1,
      offset: 16
    }
  ],
  series: []
}

export default {
  components: {
    SwitchButton,
    RectLoading
  },
  data () {
    let mountPromiseResolve = null
    const i18nPrefix = 'admin.hrDashboard'
    const mountPromise = new Promise((resolve, reject) => {
      mountPromiseResolve = resolve
    })
    return {
      channelsType: 'applications',
      mountPromise,
      i18nPrefix,
      mountPromiseResolve,
      loading: false,
      chart: null,
      channelList: [],
      total: 0,
      colorMap: {},
      colorIndex: 0,
      colorList: ['#8fc18e', '#b7cdb6', '#d2e8f5', '#b2dbed', '#c1c8e5', '#769acc', '#647eaf', '#3f2281', '#5a159a', '#a65dc8',
        '#d773db', '#db73bc', '#df619f', '#df6182', '#df6182', '#d27d4f', '#d2a54f', '#d2ca4f', '#9bc271', '#71c29f', '#718ac2'],
      chartResize: null
    }
  },
  computed: {
    echartLegend () {
      const legendList = []
      _.forEach(this.channelList, (item) => {
        if (item.total > 0) {
          legendList.push({
            name: item.i18nName,
            icon: 'square',
            itemStyle: {
              color: item.color,
              borderCap: 'round'
            }
          })
        }
      })
      return legendList
    }
  },
  created () {
    this.chartResize = _.debounce(() => this.chart.resize(), 100)
    this.loading = true
    this.channelsType = this.channelsTabStatus()
    if (this.channelsType === 'jobs') {
      this.refreshJobChart()
    } else {
      this.refreshApplicationChart()
    }
  },
  mounted () {
    this.mountPromiseResolve()
    window.addEventListener('resize', this.chartResize)
  },
  methods: {
    ...mapActions('hrDashboard', ['getChannelCountInfo', 'changeChannelsTabStatus']),
    ...mapGetters('hrDashboard', ['channelsTabStatus']),
    async refreshJobChart () {
      this.loading = true
      const resp = await this.getChannelCountInfo({ type: 'job' })
      let channelList = _.map(resp.channel, item => addI18nProperty(item, 'i18nName', 'cn_name', 'name'))
      const weChatChannel = _.remove(channelList, { id: 1 })[0]
      if (weChatChannel) {
        channelList.push(createI18nProperty({ id: weChatChannel.id, cn_name: 'weChat' }, 'i18nName', 'admin.hrDashboard.label.channelName.weChat'))
      }
      channelList.push(createI18nProperty({ id: 'referral' }, 'i18nName', 'admin.hrDashboard.label.channelName.referral'))
      channelList.push(createI18nProperty({ id: 'private' }, 'i18nName', 'admin.hrDashboard.label.channelName.notPublic'))
      channelList = this.getChannelList(channelList, resp.job)
      this.channelList = channelList
      this.everyDayData = resp.job.every_day
      await this.refreshChart()
      this.loading = false
    },
    async refreshApplicationChart () {
      this.loading = true
      const resp = await this.getChannelCountInfo({ type: 'application' })
      const application = resp.application
      let channelList = _.map(resp.channel, item => {
        addI18nProperty(item, 'i18nName', 'cn_name', 'name')
        this.$set(item, 'show', true)
        return item
      })
      channelList.push(createI18nProperty({ id: 'OTHER', cn_name: 'weChat', show: true }, 'i18nName', 'admin.hrDashboard.label.channelName.weChat'))
      const referralChannel = _.remove(channelList, { id: 1 })[0]
      if (referralChannel) {
        channelList.push(referralChannel)
      }
      channelList = this.getChannelList(channelList, application)
      this.channelList = channelList
      this.everyDayData = application.every_day
      await this.refreshChart()
      this.chart.on('legendselectchanged', this.applicationChartClickEvent)
      this.loading = false
    },
    getChannelColor (channel) {
      const name = channel.cn_name || channel.i18nName
      let color = _.find(this.colorMap, (color, channelName) => name === channelName || name.startsWith(channelName) || channelName.startsWith(name))
      if (!color) {
        if (this.colorIndex >= this.colorList.length) {
          color = this.getRandomColor()
        } else {
          color = this.colorList[this.colorIndex]
          this.colorIndex += 1
        }
        this.colorMap[name] = color
      }
      return color
    },
    getRandomColor () {
      const hr = Math.floor(Math.random() * (255 + 1)).toString(16).padStart(2, '0')
      const hg = Math.floor(Math.random() * (255 + 1)).toString(16).padStart(2, '0')
      const hb = Math.floor(Math.random() * (255 + 1)).toString(16).padStart(2, '0')
      return '#' + hr + hg + hb
    },
    getChannelList (channelList, countData) {
      const result = []
      const totalData = countData.total
      const totalCount = totalData.total
      _.forEach(channelList, (channel) => {
        const count = totalData[channel.id] || 0
        channel.total = count
        if (count > 0) {
          channel.color = this.getChannelColor(channel)
        }
        channel.percentage = Number(formatBigNumber(count / totalCount * 100, 2)) || 0
        result.push(channel)
      })
      return _.filter(result, (item) => item.total > 0)
    },
    async refreshChart () {
      if (this.chart) {
        this.chart.dispose() // destroy
      }
      const xAxisData = []
      const yAxisData = {}
      _.forEach(this.channelList, (channel) => {
        yAxisData[channel.id] = []
      })
      _.forEach(this.everyDayData, (countData, day) => {
        const dayDate = moment(day)
        xAxisData.push(dayDate.format('MM/DD'))
        _.forEach(yAxisData, (countList, channelId) => {
          let count = countData[channelId]
          if ((count || 0) <= 0) {
            count = 0
          }
          countList.push(count)
        })
      })
      await this.mountPromise
      this.chart = echarts.init(this.$refs.channelChart)
      const option = _.clone(CHART_OPTION)
      option.legend.data = this.echartLegend
      option.xAxis[0].data = xAxisData.reverse()
      option.series = _.map(this.channelList, (channel) => {
        return {
          name: channel.i18nName,
          type: 'line',
          stack: 'Total',
          symbol: 'circle',
          color: channel.color,
          areaStyle: {},
          emphasis: {
            focus: 'series'
          },
          data: yAxisData[channel.id].reverse()
        }
      }).reverse()
      this.chart.setOption(option)
    },
    handelChangeTabStatus () {
      if (this.channelsType === 'jobs') {
        this.refreshJobChart()
      } else {
        this.refreshApplicationChart()
      }
      this.changeChannelsTabStatus(this.channelsType)
    },
    applicationChartClickEvent (params) {
      const statusMapping = params.selected
      _.forEach(this.channelList, (item) => {
        if (item.total > 0 && statusMapping[item.i18nName] !== undefined) {
          item.show = statusMapping[item.i18nName]
        }
      })
    }
  },
  destroyed () {
    window.removeEventListener('resize', this.chartResize)
  },
  watch: {
    '$i18n.locale': {
      immediate: true,
      handler () {
        const locale = this.$i18n.locale
        if (locale === 'en') {
          moment.locale('en')
        } else {
          moment.locale('zh-cn')
        }
        this.$nextTick(async () => this.handelChangeTabStatus)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.channels-content {
  background: #FFFFFF;
  border-radius: 6px;
  color: #303133;
  position: relative;
  font-weight: 500;
  margin-bottom: 15px;

  .header-container {
    padding: 20px 24px 0;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .title {
      font-size: 18px;
      height: 25px;
      line-height: 25px;
      font-weight: bold;
    }
  }

  .channels-chart {
    width: 100%;
    height: 330px;
    padding: 0 18px;
  }

  .channels-chart-ul {
    padding: 30px 18px;
    width: 100%;
    .channels-chart-li {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0 14% 0 7%;
      margin-bottom: 20px;
      .channels-title {
        width: 20%;
        .color {
          display: inline-block;
          width: 25px;
          height: 25px;
          border-radius: 3px;
          margin: 2px 20px 0 0;
        }
        .name {
          display: inline-block;
          font-size: 14px;
          vertical-align: top;
          line-height: 30px;
        }
      }
      ::v-deep {
        .el-progress {
          width: 68%;
          margin: 8px 0;
          .el-progress-bar__outer {
            overflow: inherit;
            .el-progress-bar__innerText {
              margin-top: -35px;
              font-size: 14px;
              font-weight: bold;
              color: #000;
            }
          }
        }
      }
      .channels-title-number {
        font-size: 18px;
        font-weight: bold;
        text-align: right;
        display: block;
        width: 60px;
      }
    }
  }
}

#main {
  .loading {
    position: absolute;
    z-index: 1;
  }
}
</style>
